/**
 * @typedef {import('../../../../types/AuthContext').default} Auth
 * @typedef {import('../../../Agent/GetContact').default} ContactData
 * @typedef {import('../../../Agent/agent-service').Contact} ContactFormValues
 * @typedef {import('../../../shared/AdministratorService/GetsAgentsByGroupIds').Agent} Agent
 * @typedef {import('./EditContact').Props} Props
 * @typedef {import('./EditContact').State} State
 */

import { AuthContext } from '../../../../shared/AuthProvider';
import {
	assignContactTags,
	createContactActivity,
	createContactTag,
	getContact,
	getContactActivity,
	getContactHomeownerProperties,
	getContactTags,
} from '../../../Agent/agent-service';
import { Redirect } from 'react-router';
import { translate } from '../../../Internationalization';
import { withContactService } from '../../../../service-container';
import BaseButton from '../../../../shared/BaseButton/BaseButton';
import captureError from '../../../../utils/captureError';
import ContactFinancesAndValuation from '../../../shared/ContactFinancesAndValuation';
import ContactForm from '../../../Admin/Users/Contacts/ContactForm';
import ContactLimitPopup from '../../../shared/ContactLimitPopup/ContactLimitPopup';
import FormErrorMessages from '../../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../../shared/Forms/Messages/FormSuccessMessages';
import formatDateForDisplay from '../../../../utils/formatDateForDisplay';
import formatDateStringForDatepicker from '../../../../utils/formatDateStringForDatepicker';
import images from '../../../../config/local/images';
import LinkButton from '../../../../shared/LinkButton';
import Loading from '../../../../shared/Loading';
import paths from '../../../../config/local/paths';
import React from 'react';
import ResendInviteButton from '../../../shared/ResendInviteButton';

/** @extends {React.Component<Props, State>} */
class EditContact extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		/** @type {State} */
		this.state = {
			activities: [],
			activityComment: '',
			activityErrorMessage: null,
			activitySuccessMessage: '',
			availableTags: [],
			contactData: null,
			errorMessage: null,
			formComplete: false,
			initialGroupValues: null,
			isEditing: false,
			isGettingData: true,
			isGettingProperties: false,
			isManagingTags: false,
			isSavingActivity: false,
			isSavingTags: false,
			newTagName: '',
			properties: [],
			resendInviteMessage: null,
			selectedPropertyId: '',
			showLimitContactPopup: false,
			tagErrorMessage: null,
			tagIdsDraft: [],
			tagSuccessMessage: '',
		};

		this.handleActivityCommentChange =
			this.#handleActivityCommentChange.bind(this);
		this.handleCreateActivity = this.#handleCreateActivity.bind(this);
		this.handleCreateTag = this.#handleCreateTag.bind(this);
		this.handleRemoveTag = this.#handleRemoveTag.bind(this);
		this.handleResendInvite = this.handleResendInvite.bind(this);
		this.handleSaveTags = this.#handleSaveTags.bind(this);
		this.handleContactMortgageSaved =
			this.#handleContactMortgageSaved.bind(this);
		this.handlePropertySelect = this.#handlePropertySelect.bind(this);
		this.handleTagDraftToggle = this.#handleTagDraftToggle.bind(this);
		this.handleTagNameChange = this.#handleTagNameChange.bind(this);
		this.handleToggleEdit = this.handleToggleEdit.bind(this);
		this.handleToggleTagManager = this.#handleToggleTagManager.bind(this);
		this._handleLimitContactClose =
			this._handleLimitContactClose.bind(this);
		this._handleSubmit = this._handleSubmit.bind(this);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			const accessToken = /** @type {{accessToken: string}} */ (
				this.context
			).accessToken;

			const contactResponse = await getContact(
				this.props.match.params.contactId,
				accessToken
			);
			const [agentList, activities, availableTags] = await Promise.all([
				this.#getAvailableAgents().catch((error) => {
					captureError(error);

					return [];
				}),
				getContactActivity(
					this.props.match.params.contactId,
					accessToken
				).catch((error) => {
					captureError(error);

					return [];
				}),
				getContactTags(accessToken, {
					contactId: this.props.match.params.contactId,
				}).catch((error) => {
					captureError(error);

					return [];
				}),
			]);
			const properties = contactResponse.homeownerId
				? await getContactHomeownerProperties(
						contactResponse.homeownerId,
						accessToken
				  ).catch((error) => {
						captureError(error);

						return [];
				  })
				: [];
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			const initialGroupValues = EditContact.#getInitialGroupValues(
				contactResponse,
				agentList
			);

			const moveDate = contactResponse?.moveDate
				? formatDateStringForDatepicker(contactResponse?.moveDate)
				: '';
			const closeDate = contactResponse?.closeDate
				? formatDateStringForDatepicker(contactResponse?.closeDate)
				: '';

			this.setState({
				activities,
				availableTags,
				contactData: { ...contactResponse, closeDate, moveDate },
				initialGroupValues,
				isGettingData: false,
				properties,
				selectedPropertyId: selectedProperty?.id ?? '',
				tagIdsDraft: contactResponse?.tagIds ?? [],
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {ContactData} contactData
	 * @param {Agent[]} agentList
	 * @returns {{agent: Agent | null, agentList: Agent[]}}
	 */
	static #getInitialGroupValues(contactData, agentList) {
		const agent = agentList.find(
			/**
			 * @param  {Agent} groupAgent
			 * @returns {boolean}
			 */
			(groupAgent) => groupAgent.id === contactData.agentId
		);

		return {
			agent: agent ?? null,
			agentList,
		};
	}

	/**
	 * @returns {Promise<Agent[]>}
	 */
	async #getAvailableAgents() {
		/** @type {{id: string; name: string}[]} */
		const allowedGroups =
			await this.props.administratorService.getAllowedGroups(50);

		if (allowedGroups.length === 0) {
			return [];
		}

		const agents =
			await this.props.administratorService.getAgentsByGroupIds(
				allowedGroups.map((group) => group.id)
			);

		return EditContact.#dedupeAgents(agents);
	}

	/**
	 * @param {Agent[]} agentList
	 * @returns {Agent[]}
	 */
	static #dedupeAgents(agentList) {
		const seen = new Set();

		return agentList.filter((agent) => {
			if (!agent?.id || seen.has(agent.id)) {
				return false;
			}

			seen.add(agent.id);

			return true;
		});
	}

	/**
	 * @param {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties']} properties
	 * @returns {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0] | undefined}
	 */
	static #getDefaultProperty(properties) {
		return (
			properties.find((property) => property.isPrimary) ??
			properties.find(Boolean)
		);
	}

	/**
	 * @param {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]} property
	 * @param {object} details
	 * @returns {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]}
	 */
	static #mergeMortgageDetails(property, details) {
		return {
			...property,
			details: {
				...property.details,
				...details,
			},
		};
	}

	/**
	 * @protected
	 * @param {ContactFormValues} values
	 * @returns {Promise<void>}
	 */
	async _handleSubmit(values) {
		try {
			const accessToken = /** @type {{accessToken: string}} */ (
				this.context
			).accessToken;

			await this.props.contactService.updateContact(
				this.props.match.params.contactId,
				accessToken,
				values
			);

			this.setState({
				errorMessage: null,
				formComplete: true,
				isEditing: false,
			});
		} catch (error) {
			// @ts-ignore -- status code added to error obj
			if (error?.statusCode === 403) {
				this.setState({
					showLimitContactPopup: true,
				});
				return;
			}
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {React.ChangeEvent<HTMLSelectElement>} event
	 * @returns {Promise<void>}
	 */
	async handleResendInvite(event) {
		event.preventDefault();
		if (!this.state.contactData?.id) {
			return;
		}

		try {
			await this.props.contactService.inviteContact(
				this.state.contactData?.id
			);

			this.setState((prevState) => ({
				resendInviteMessage: translate(
					'admin.pages.contacts.edit.invite.success',
					prevState.contactData?.firstName ?? '',
					prevState.contactData?.lastName ?? ''
				),
			}));
		} catch (error) {
			captureError(error);

			this.setState({
				resendInviteMessage: translate(
					'admin.pages.contacts.edit.invite.error'
				),
			});
		}
	}

	/**
	 * @returns {void}
	 */
	handleToggleEdit() {
		this.setState((prevState) => ({
			activityErrorMessage: null,
			activitySuccessMessage: '',
			errorMessage: null,
			isEditing: !prevState.isEditing,
		}));
	}

	/**
	 * @param {string | null | undefined} homeownerId
	 * @returns {Promise<void>}
	 */
	async #setContactProperties(homeownerId) {
		if (!homeownerId) {
			return;
		}

		this.setState({ isGettingProperties: true });

		try {
			const properties = await getContactHomeownerProperties(
				homeownerId,
				this.context.accessToken
			);
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			this.setState({
				isGettingProperties: false,
				properties,
				selectedPropertyId: selectedProperty?.id ?? '',
			});
		} catch (error) {
			captureError(error);
			this.setState({
				isGettingProperties: false,
			});
		}
	}

	/**
	 * @param {string} propertyId
	 * @returns {void}
	 */
	#handlePropertySelect(propertyId) {
		this.setState({
			selectedPropertyId: propertyId,
		});
	}

	/**
	 * @param {string} propertyId
	 * @param {object} mortgageDetails
	 * @returns {Promise<void>}
	 */
	async #handleContactMortgageSaved(propertyId, mortgageDetails) {
		await this.#setContactProperties(this.state.contactData?.homeownerId);

		this.setState((prevState) => ({
			properties: prevState.properties.map((property) => {
				if (property.id !== propertyId) {
					return property;
				}

				return EditContact.#mergeMortgageDetails(
					property,
					mortgageDetails
				);
			}),
			selectedPropertyId: propertyId,
		}));
	}

	/**
	 * @param {React.ChangeEvent<HTMLTextAreaElement>} event
	 * @returns {void}
	 */
	#handleActivityCommentChange(event) {
		this.setState({
			activityComment: event.target.value,
			activityErrorMessage: null,
			activitySuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateActivity() {
		const description = this.state.activityComment.trim();

		if (!description) {
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_required'
				),
				activitySuccessMessage: '',
			});
			return;
		}

		try {
			this.setState({
				activityErrorMessage: null,
				activitySuccessMessage: '',
				isSavingActivity: true,
			});

			await createContactActivity(
				this.props.match.params.contactId,
				description,
				this.context.accessToken
			);

			const activities = await getContactActivity(
				this.props.match.params.contactId,
				this.context.accessToken
			);

			this.setState({
				activities,
				activityComment: '',
				activityErrorMessage: null,
				activitySuccessMessage: translate(
					'agent.pages.contacts.edit.activity.create_success'
				),
				isSavingActivity: false,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_error'
				),
				activitySuccessMessage: '',
				isSavingActivity: false,
			});
		}
	}

	/**
	 * @returns {void}
	 */
	#handleToggleTagManager() {
		this.setState((prevState) => ({
			isManagingTags: !prevState.isManagingTags,
			newTagName: '',
			tagErrorMessage: null,
			tagIdsDraft: prevState.isManagingTags
				? prevState.contactData?.tagIds ?? []
				: [...(prevState.contactData?.tagIds ?? [])],
			tagSuccessMessage: prevState.isManagingTags
				? prevState.tagSuccessMessage
				: '',
		}));
	}

	/**
	 * @param {string} tagId
	 * @returns {void}
	 */
	#handleTagDraftToggle(tagId) {
		this.setState((prevState) => {
			const tagIdSet = new Set(prevState.tagIdsDraft);

			if (tagIdSet.has(tagId)) {
				tagIdSet.delete(tagId);
			} else {
				tagIdSet.add(tagId);
			}

			return {
				tagErrorMessage: null,
				tagIdsDraft: [...tagIdSet],
				tagSuccessMessage: '',
			};
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	#handleTagNameChange(event) {
		this.setState({
			newTagName: event.target.value,
			tagErrorMessage: null,
			tagSuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateTag() {
		const tagName = this.state.newTagName.trim();

		if (!tagName) {
			this.setState({
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_required'
				),
			});
			return;
		}

		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tag = await createContactTag(
				tagName,
				this.context.accessToken,
				{ contactId: this.props.match.params.contactId }
			);
			const nextTagIds = [
				...new Set([...(this.state.contactData?.tagIds ?? []), tag.id]),
			];

			await assignContactTags(
				this.props.match.params.contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				availableTags: [...prevState.availableTags, tag],
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds: nextTagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				newTagName: '',
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.create_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_error'
				),
			});
		}
	}

	/**
	 * @param {string} tagId
	 * @returns {Promise<void>}
	 */
	async #handleRemoveTag(tagId) {
		try {
			const currentTagIds = this.state.contactData?.tagIds ?? [];
			const nextTagIds = currentTagIds.filter(
				(currentTagId) => currentTagId !== tagId
			);

			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			await assignContactTags(
				this.props.match.params.contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds: nextTagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.remove_error'
				),
			});
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleSaveTags() {
		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tagIds = [...new Set(this.state.tagIdsDraft)];

			await assignContactTags(
				this.props.match.params.contactId,
				tagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: tagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.save_error'
				),
			});
		}
	}

	/**
	 * @protected
	 * @returns {void}
	 */
	_handleLimitContactClose() {
		this.setState({ showLimitContactPopup: false });
	}

	/**
	 * @returns {JSX.Element}
	 */
	static #renderContactPopupContent() {
		const contactLimit = 'global.contact_limit';
		const contactCap = process.env.REACT_APP_CONTACT_CAP ?? '100';

		return (
			<React.Fragment>
				<div>
					{translate(
						`global.contact_limit.assign.header`,
						contactCap
					)}{' '}
					<span className="font-bold">
						{translate(`${contactLimit}.assign.body`)}
					</span>
				</div>

				<div>
					{translate(
						`global.contact_limit.assign.group_footer`,
						contactCap
					)}
				</div>
			</React.Fragment>
		);
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	static #formatValue(value) {
		return value || '—';
	}

	/**
	 * @param {string} label
	 * @param {string | null | undefined} value
	 * @param {string} [className]
	 * @returns {JSX.Element}
	 */
	static #renderReadOnlyField(label, value, className = '') {
		return (
			<section className={className || ''}>
				<label className="input-label">{label}</label>
				<div className="min-h-[3.2rem] py-1.5 text-ms-0 leading-normal text-grey-900">
					{EditContact.#formatValue(value)}
				</div>
			</section>
		);
	}

	/**
	 * @param {string[] | undefined} tagIds
	 * @returns {{ id: string; isDefault: boolean; name: string }[]}
	 */
	#getSelectedTags(tagIds = this.state.contactData?.tagIds ?? []) {
		const selectedTagIdSet = new Set(tagIds);

		return this.state.availableTags.filter((tag) =>
			selectedTagIdSet.has(tag.id)
		);
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	static #formatActivityTimestamp(value) {
		return value ? formatDateForDisplay(value) : '—';
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderActivity() {
		return (
			<section className="h-full">
				<header className="flex items-center justify-between gap-4 mb-4">
					<h2 className="h-ms-5">
						{translate('agent.pages.contacts.edit.activity.title')}
					</h2>
				</header>

				<FormSuccessMessages
					className="mb-4"
					messages={this.state.activitySuccessMessage}
				/>
				<FormErrorMessages
					className="mb-4"
					messages={[this.state.activityErrorMessage]}
				/>

				<section className="border border-theme rounded p-4 mb-6">
					<section className="input-group mb-4">
						<label
							className="input-label"
							htmlFor="contact-activity-comment"
						>
							{translate(
								'agent.pages.contacts.edit.activity.create_label'
							)}
						</label>
						<textarea
							className="input min-h-32"
							id="contact-activity-comment"
							onChange={this.handleActivityCommentChange}
							placeholder={translate(
								'agent.pages.contacts.edit.activity.create_placeholder'
							)}
							value={this.state.activityComment}
						/>
					</section>

					<footer className="button-group">
						<BaseButton
							disabled={
								this.state.isSavingActivity ||
								!this.state.activityComment.trim()
							}
							onClick={this.handleCreateActivity}
							type="button"
						>
							{this.state.isSavingActivity
								? translate(
										'agent.pages.contacts.edit.activity.saving'
								  )
								: translate(
										'agent.pages.contacts.edit.activity.create'
								  )}
						</BaseButton>
					</footer>
				</section>

				{this.state.activities.length ? (
					<section className="border-l-2 border-theme pl-4 space-y-3">
						{this.state.activities.map((activity) => (
							<article
								className="text-sm leading-6"
								key={activity.id}
							>
								<div className="text-sm">
									{EditContact.#formatActivityTimestamp(
										activity.createdAt
									)}{' '}
									<span className="font-subheader font-bold">
										{'('}
										{activity.sourceLabel}
										{')'}
									</span>
									{' - '}
									<span>{activity.description}</span>
								</div>
							</article>
						))}
					</section>
				) : (
					<input
						className="input input-disabled"
						disabled={true}
						readOnly={true}
						type="text"
						value={translate(
							'agent.pages.contacts.edit.activity.empty'
						)}
					/>
				)}
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	// eslint-disable-next-line complexity
	#renderDetails() {
		if (!this.state.contactData) {
			return null;
		}

		const contact = this.state.contactData;
		const agentName = this.state.initialGroupValues?.agent?.name ?? '—';
		const draftSelectedTags = this.#getSelectedTags(this.state.tagIdsDraft);
		const selectedTags = this.#getSelectedTags();

		return (
			<React.Fragment>
				<header className="flex items-center justify-between mt-10 mb-6">
					<h1 className="hl-ms-6">
						{translate('admin.pages.contacts.edit.title')}
					</h1>
					<section className="button-group h-fit">
						<BaseButton
							onClick={this.handleToggleEdit}
							outline={true}
							type="button"
						>
							{translate('global.forms.buttons.edit')}
						</BaseButton>
						<LinkButton
							icon={images.icons.delete}
							to={paths.app.admin.contacts.view}
						>
							{translate('global.forms.buttons.cancel')}
						</LinkButton>
					</section>
				</header>

				<fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
					<legend className="h-ms-5 mb-6">
						{translate('admin.pages.contacts.form.contact_title')}
					</legend>
					{EditContact.#renderReadOnlyField(
						translate('admin.pages.contacts.form.agent.label'),
						agentName,
						'col-span-full input-group'
					)}
					{EditContact.#renderReadOnlyField(
						'First Name',
						contact.firstName
					)}
					{EditContact.#renderReadOnlyField(
						'Last Name',
						contact.lastName
					)}
					{EditContact.#renderReadOnlyField(
						translate('global.forms.inputs.email.label'),
						contact.email,
						'col-span-full input-group'
					)}
					{EditContact.#renderReadOnlyField(
						'Landline Phone',
						contact.landline
					)}
					{EditContact.#renderReadOnlyField(
						'Mobile Phone',
						contact.mobile
					)}
				</fieldset>

				<fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
					<legend className="h-ms-5 mb-6">
						{translate('global.forms.inputs.address.section_title')}
					</legend>
					{EditContact.#renderReadOnlyField(
						'Street Address',
						contact.streetAddress1
					)}
					{EditContact.#renderReadOnlyField(
						'Address Designation',
						contact.streetAddress2
					)}
					{EditContact.#renderReadOnlyField('City', contact.city)}
					{EditContact.#renderReadOnlyField('State', contact.state)}
					{EditContact.#renderReadOnlyField(
						'Postal Code',
						contact.postalCode
					)}
					{EditContact.#renderReadOnlyField(
						'Country',
						contact.country
					)}
				</fieldset>

				<fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
					<legend className="h-ms-5 mb-6">
						{translate('admin.pages.contacts.form.moving_title')}
					</legend>
					{EditContact.#renderReadOnlyField(
						translate('agent.pages.contacts.form.move_date.label'),
						contact.moveDate
							? formatDateForDisplay(contact.moveDate)
							: ''
					)}
					{EditContact.#renderReadOnlyField(
						translate('agent.pages.contacts.form.close_date.label'),
						contact.closeDate
							? formatDateForDisplay(contact.closeDate)
							: ''
					)}
				</fieldset>

				<section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-start">
					<section>
						<header className="flex items-center justify-between gap-4 mb-4">
							<h2 className="h-ms-5">
								{translate(
									'agent.pages.contacts.edit.tags.title'
								)}
							</h2>
							<BaseButton
								disabled={this.state.isSavingTags}
								onClick={this.handleToggleTagManager}
								outline={true}
								type="button"
							>
								{this.state.isManagingTags
									? translate(
											'agent.pages.contacts.edit.tags.done'
									  )
									: translate(
											'agent.pages.contacts.edit.tags.manage'
									  )}
							</BaseButton>
						</header>

						{!!this.state.tagSuccessMessage && (
							<FormSuccessMessages
								className="mb-4"
								messages={this.state.tagSuccessMessage}
							/>
						)}
						<FormErrorMessages
							className="mb-4"
							messages={[this.state.tagErrorMessage]}
						/>

						<div className="input-group">
							{selectedTags.length > 0 ? (
								<div className="flex flex-wrap gap-3">
									{selectedTags.map((tag) => (
										<span
											className="inline-flex items-center rounded-full border border-theme px-3 py-1 text-theme font-subheader font-bold"
											key={tag.id}
										>
											<span>{tag.name}</span>
											<BaseButton
												className="ml-2 h-auto min-h-0 w-auto min-w-0 border-0 bg-transparent px-0 py-0 text-theme shadow-none"
												disabled={
													this.state.isSavingTags
												}
												onClick={() =>
													this.handleRemoveTag(tag.id)
												}
												type="button"
											>
												{'x'}
											</BaseButton>
										</span>
									))}
								</div>
							) : (
								<input
									className="input input-disabled"
									disabled={true}
									readOnly={true}
									type="text"
									value="—"
								/>
							)}
						</div>

						{this.state.isManagingTags ? (
							<section className="mt-6 border border-theme p-4 rounded">
								<h3 className="font-subheader font-bold mb-3">
									{translate(
										'agent.pages.contacts.edit.tags.assign'
									)}
								</h3>

								{this.state.availableTags.length > 0 ? (
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
										{this.state.availableTags.map((tag) => (
											<label
												className="flex items-center gap-3"
												htmlFor={`contact-tag-${tag.id}`}
												key={tag.id}
											>
												<input
													checked={this.state.tagIdsDraft.includes(
														tag.id
													)}
													id={`contact-tag-${tag.id}`}
													onChange={() =>
														this.handleTagDraftToggle(
															tag.id
														)
													}
													type="checkbox"
												/>
												<span>
													{tag.name}
													{tag.isDefault
														? ' (Default)'
														: ''}
												</span>
											</label>
										))}
									</div>
								) : null}

								{draftSelectedTags.length > 0 ? (
									<div className="mb-4 text-sm text-grey-700">
										{translate(
											'agent.pages.contacts.edit.tags.selected'
										)}
										{': '}
										{draftSelectedTags
											.map((tag) => tag.name)
											.join(', ')}
									</div>
								) : null}

								<section className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 mb-4 items-end">
									<section className="input-group mb-0">
										<label className="input-label">
											{translate(
												'agent.pages.contacts.edit.tags.create_custom'
											)}
										</label>
										<input
											className="input"
											onChange={this.handleTagNameChange}
											placeholder={translate(
												'agent.pages.contacts.edit.tags.create_placeholder'
											)}
											type="text"
											value={this.state.newTagName}
										/>
									</section>
									<BaseButton
										disabled={
											this.state.isSavingTags ||
											!this.state.newTagName.trim()
										}
										onClick={this.handleCreateTag}
										type="button"
									>
										{translate(
											'agent.pages.contacts.edit.tags.create'
										)}
									</BaseButton>
								</section>

								<footer className="button-group">
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleSaveTags}
										type="button"
									>
										{this.state.isSavingTags
											? translate(
													'agent.pages.contacts.edit.tags.saving'
											  )
											: translate(
													'agent.pages.contacts.edit.tags.save'
											  )}
									</BaseButton>
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleToggleTagManager}
										outline={true}
										type="button"
									>
										{translate(
											'global.forms.buttons.cancel'
										)}
									</BaseButton>
								</footer>
							</section>
						) : null}
					</section>

					{this.#renderActivity()}
				</section>

				{!!this.state.resendInviteMessage && (
					<div className="mb-6 text-sm text-theme">
						{this.state.resendInviteMessage}
					</div>
				)}

				{this.#renderFinancesAndValuation()}
			</React.Fragment>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderFinancesAndValuation() {
		if (!this.state.contactData?.homeownerId) {
			return null;
		}

		return (
			<ContactFinancesAndValuation
				accessToken={this.context.accessToken}
				agentId={this.state.contactData.agentId ?? ''}
				contactId={this.props.match.params.contactId}
				homeownerId={this.state.contactData.homeownerId}
				isGettingProperties={this.state.isGettingProperties}
				onMortgageSaved={this.handleContactMortgageSaved}
				onPropertySelect={this.handlePropertySelect}
				properties={this.state.properties}
				selectedPropertyId={this.state.selectedPropertyId}
				userId={this.context.userId}
			/>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		if (this.state.formComplete) {
			return (
				<Redirect
					to={{
						pathname: paths.app.admin.contacts.view,
						state: {
							message: translate(
								'admin.pages.contacts.edit.success_message'
							),
						},
					}}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		return (
			<React.Fragment>
				<ContactLimitPopup
					onClose={this._handleLimitContactClose}
					showPopup={this.state.showLimitContactPopup}
					title={translate(`global.contact_limit.assign.title`)}
					to={paths.app.admin.groups.root}
				>
					{EditContact.#renderContactPopupContent()}
				</ContactLimitPopup>

				{this.state.isEditing ? (
					<React.Fragment>
						<ContactForm
							additionalButtons={[
								<ResendInviteButton
									icon={images.icons.plus}
									key={0}
									onResendInvite={this.handleResendInvite}
									outline={true}
									size="large"
								/>,
							]}
							contactData={this.state.contactData}
							error={this.state?.errorMessage}
							formTitle={translate(
								'admin.pages.contacts.edit.title'
							)}
							initialGroupValues={this.state.initialGroupValues}
							onResendInvite={this.handleResendInvite}
							onSubmit={this._handleSubmit}
							resendInviteMessage={this.state.resendInviteMessage}
							submitText={translate(
								'admin.pages.contacts.edit.update'
							)}
						/>

						{this.#renderFinancesAndValuation()}
					</React.Fragment>
				) : (
					this.#renderDetails()
				)}
			</React.Fragment>
		);
	}
}

export default /** @type {React.ComponentType<any>} */ (
	withContactService(EditContact)
);

EditContact.contextType = AuthContext;
